import React from 'react';
import { Select, Button } from 'antd';
import WeekSelectorModal from './WeekSelectorModal';
import { DateRange } from '../../services/DashboardService';
import { formatDisplayDate } from '../../utils/formatters';

interface DashboardFiltersProps {
  viewMode: 'weekly' | 'quarterly';
  sortBy: string;
  loading: boolean;
  isMobile: boolean;
  dateRanges: DateRange[];
  selectedDateRange: DateRange | null;
  availableQuarters: string[];
  selectedQuarter: { quarter: string; year: number } | null;
  isWeekSelectorOpen: boolean;
  onViewModeChange: (value: 'weekly' | 'quarterly') => void;
  onSortByChange: (value: string) => void;
  onDateRangeSelect: (range: DateRange) => void;
  onQuarterSelect: (quarter: string, year: number) => void;
  onWeekSelectorOpen: () => void;
  onWeekSelectorClose: () => void;
  getBaselinePeriod: (startDate: string, endDate: string) => {
    baselineStart: string;
    baselineEnd: string;
    validStart: string;
    validEnd: string;
  } | null;
  getButtonText: () => string;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  viewMode,
  sortBy,
  loading,
  isMobile,
  dateRanges,
  selectedDateRange,
  availableQuarters,
  selectedQuarter,
  isWeekSelectorOpen,
  onViewModeChange,
  onSortByChange,
  onDateRangeSelect,
  onQuarterSelect,
  onWeekSelectorOpen,
  onWeekSelectorClose,
  getBaselinePeriod,
  getButtonText,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 8,
        justifyContent: isMobile ? 'flex-start' : 'flex-end',
        alignItems: isMobile ? 'stretch' : 'center',
        position: 'relative',
        zIndex: 10,
        flexWrap: 'wrap',
      }}
    >
      <div data-tour="view-toggle">
        <Select
          value={viewMode}
          style={{ width: isMobile ? '100%' : 120 }}
          onChange={(value) => onViewModeChange(value)}
          disabled={loading}
          options={[
            { label: 'Weekly', value: 'weekly' },
            { label: 'Quarterly', value: 'quarterly' },
          ]}
        />
      </div>
      <Select
        value={sortBy}
        style={{ width: isMobile ? '100%' : 180 }}
        onChange={(value) => onSortByChange(value)}
        disabled={loading}
        options={[
          { label: 'Sales (High to Low)', value: 'sales' },
          { label: 'DM', value: 'dm' },
        ]}
      />
      <div data-tour="date-selector">
        {viewMode === 'weekly' ? (
          <WeekSelectorModal
            open={isWeekSelectorOpen}
            dateRanges={dateRanges}
            selectedRange={selectedDateRange}
            onSelect={onDateRangeSelect}
            onClose={onWeekSelectorClose}
            getBaselinePeriod={getBaselinePeriod}
            formatDisplayDate={formatDisplayDate}
          >
            <Button
              style={{
                width: isMobile ? '100%' : 'auto',
                minWidth: 200,
                maxWidth: 500,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
              onClick={onWeekSelectorOpen}
              disabled={loading || dateRanges.length === 0}
            >
              <span
                style={{
                  textAlign: 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                }}
              >
                {getButtonText()}
              </span>
            </Button>
          </WeekSelectorModal>
        ) : (
          <Select
          value={selectedQuarter && !isNaN(selectedQuarter.year) 
            ? `${selectedQuarter.quarter} ${selectedQuarter.year}` 
            : undefined}
          style={{ width: isMobile ? '100%' : 200 }}
          onChange={(value) => {
            // Backend returns format "Q2 2025" (space-separated), not "Q2-2025"
            const quarterStr = value.trim();
            const match = quarterStr.match(/^(Q[1-4])\s+(\d{4})$/i);
            if (match) {
              const [, quarter, yearStr] = match;
              const year = parseInt(yearStr, 10);
              if (!isNaN(year)) {
                onQuarterSelect(quarter, year);
                return;
              }
            }
            // Fallback: try splitting by space or dash
            const parts = quarterStr.split(/\s+|-/);
            if (parts.length >= 2) {
              const quarter = parts[0];
              const yearStr = parts[parts.length - 1];
              const year = parseInt(yearStr, 10);
              if (!isNaN(year)) {
                onQuarterSelect(quarter, year);
                return;
              }
            }
            console.error('Failed to parse quarter string:', value);
          }}
          disabled={loading || availableQuarters.length === 0}
          placeholder="Select Quarter"
          options={availableQuarters.map((quarterYear) => {
            // Backend returns format "Q2 2025" (space-separated), not "Q2-2025"
            const quarterStr = quarterYear.trim();
            const match = quarterStr.match(/^(Q[1-4])\s+(\d{4})$/i);
            if (match) {
              const [, quarter, year] = match;
              return {
                label: `${quarter} ${year}`,
                value: quarterYear,
              };
            }
            // Fallback: try splitting by space or dash
            const parts = quarterStr.split(/\s+|-/);
            if (parts.length >= 2) {
              const quarter = parts[0];
              const year = parts[parts.length - 1];
              return {
                label: `${quarter} ${year}`,
                value: quarterYear,
              };
            }
            // If parsing fails, return as-is
            return {
              label: quarterYear,
              value: quarterYear,
            };
          })}
        />
        )}
      </div>
    </div>
  );
};

